/**
 * Supposedly Uppy supports AWS much better out of the box, even with TUS,which has a ruby
 * TUS server implementation. The ruby TUS server also supposedly works out of the box with AWS.
 * 
 * Used with AWS - Uppy should be able to handle chunking uploads in a straightforward way.
 * Therefore a user should be able to upload a very big file directly to AWS buckets without
 * worrying about his connection being too slow, or even resetting between chunks.
 * 
 * This makes it possible for the user to close the upload dialog, and do something else, while
 * the file is being uploaded in the background - similar to what it looks like in Google Drive,
 * where the file is uploading in the bottom right corner in the background.
 * This also makes it possible to pause an upload on the frontend.
 * 
 * Ruby TUS server also handles the ability to pause/resume uploads between sessions, as well as
 * giving the ability to track upload progress on the backend. The user can now close their browser,
 * and open it again and continue the upload.
 * 
 * In addition Uppy gives us the ability to upload files directly from MS Office and Google Drive, etc.
 * 
 * None of these functionalities however seem to come out of the box for Google Cloud.
 * Therefore this file is a very very simple custom implementation for direct upload to GCP.
 * 
 * There is no chunking, and progress is tracked manually - and it is NOT related to actual file
 * upload progress. File upload progress is boolean. The UI progress presented to the user is more of
 * a visual representation that something is happening - and tracks the steps - like getting
 * a signed url, uploading the file, creating the folder file model instance, etc.
 */

export const uploadFileToGCP = async (presignedUrl, file, fileData) => {
  // Upload to storage
  const uploadResponse = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type
    },
    body: fileData
  });
  if (!uploadResponse.ok) {
    throw new Error(`Upload failed with status: ${uploadResponse.status}`);
  }
  return uploadResponse;
};
export const readFileData = async file => {
  const reader = new FileReader();

  // Create a promise to handle the FileReader
  const readFilePromise = new Promise((resolve, reject) => {
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => {
      reject(new Error('Failed to read file data'));
    };
    reader.readAsArrayBuffer(file.data);
  });

  // Get file data as ArrayBuffer
  const fileData = await readFilePromise;
  return fileData;
};
export const uploadFile = async (uppyInstance, file, getPresignedUrl, createFolderFileWithCloudFileRecord) => {
  try {
    uppyInstance.emit('manual-progress-update', file, 0);

    // Get presigned URL
    const presignedData = await getPresignedUrl(file.name, file.type);
    uppyInstance.emit('manual-progress-update', file, 2);
    if (!presignedData || !presignedData.url || !presignedData.key) {
      throw new Error('Invalid presigned URL data');
    }
    const {
      url,
      key
    } = presignedData;

    // Store key in file meta
    file.meta.key = key;

    // Check if file.data exists
    if (!file.data) {
      throw new Error('File data is missing');
    }
    const fileData = await readFileData(file);
    uppyInstance.emit('manual-progress-update', file, 5);
    const uploadResponse = await uploadFileToGCP(url, file, fileData);
    uppyInstance.emit('manual-progress-update', file, 90);

    // Create cloud file record
    const cloudFileRecord = {
      name: file.name,
      key: key,
      content_type: file.type,
      size: file.size
    };
    await createFolderFileWithCloudFileRecord(cloudFileRecord, file);
    uppyInstance.emit('manual-progress-update', file, 100);

    // Mark as successful
    uppyInstance.emit('upload-success', file, {
      status: uploadResponse.status,
      body: cloudFileRecord,
      cloudFileRecord,
      uploadURL: url
    });
  } catch (error) {
    // console.error('Error processing file:', file.name, error)
    uppyInstance.emit('upload-error', file, error);
  }
};
export const uploadAllSelectedFiles = async (uppyInstance, getPresignedUrl, createFolderFileWithCloudFileRecord) => {
  // Get all files from Uppy
  const files = uppyInstance.getFiles();
  if (files.length === 0) {
    console.error('No files to upload');
    return false;
  }

  // Process each file
  for (const file of files) {
    await uploadFile(uppyInstance, file, getPresignedUrl, createFolderFileWithCloudFileRecord);
  }

  // Get successful and failed files
  const allFiles = uppyInstance.getFiles();
  const successfulFiles = allFiles.filter(file => file.progress && file.progress.uploadComplete);
  const failedFiles = allFiles.filter(file => !file.progress || !file.progress.uploadComplete);

  // Emit complete event
  uppyInstance.emit('complete', {
    successful: successfulFiles,
    failed: failedFiles
  });
};